import classNames from 'classnames';
import { isArray } from 'lodash';
import { CSSProperties, FocusEvent, ReactNode, useCallback, useId, useMemo, useState } from 'react';
import ReactSelect, { OptionProps, StylesConfig, components, createFilter } from 'react-select';
import { styles } from './SelectPaginate';

export interface SelectOption<T, L = ReactNode> {
  value: T;
  label: L;
}

const Option = ({ children, isSelected, ...props }: OptionProps) => (
  <components.Option
    isSelected={isSelected}
    className={classNames('text-[#6366F1]', props.className)}
    {...props}
  >
    {/*{isSelected && (*/}
    {/*  <span className="absolute flex items-center justify-center right-4">*/}
    {/*    <img src={CheckIcon} alt="selected" />*/}
    {/*  </span>*/}
    {/*)}*/}
    {children}
  </components.Option>
);

function normalize(val: any): number | string {
  if (val && typeof val === 'object' && ('id' in val || 'toString' in val)) {
    //ToDo: add more attributes: id, name ..
    return val?.id || val?.toString();
  }

  if (val && typeof val === 'object') {
    return val?.toString();
  }

  return val;
}

interface BaseProps<T> {
  name?: string;
  id?: string;
  options: SelectOption<T>[];
  optionStyles?: CSSProperties;
  defaultValue?: T;
  className?: string;
  selectStyles?: StylesConfig;
  isLoading?: boolean;
  label?: string;
  classNameLabel?: string;
  error?: string;
  positionMenu?: 'up' | 'down';
  position?: 'relative' | 'absolute';
  disabled?: boolean;
  placeholder?: string;
  withBorder?: boolean;
  showBorderBottom?: boolean;
  icon?: string;
  autoHeight?: boolean;
  bgTransparent?: boolean;
  rounded?: 'full' | 'right' | 'left';
}

export type SelectProps<
  T,
  Multi extends boolean = false,
  Clearable extends boolean = false,
> = BaseProps<T> & {
  isMulti?: Multi;
  isClearable?: Clearable;
  value?: Multi extends true
    ? Clearable extends true
      ? T[] | null
      : T[]
    : Clearable extends true
    ? T | null
    : T;
  onChange?: Multi extends true
    ? Clearable extends true
      ? (value: T[] | null) => void
      : (value: T[]) => void
    : Clearable extends true
    ? (value: T | null) => void
    : (value: T) => void;
};

export const Select = <T, Multi extends boolean = false, Clearable extends boolean = false>({
  id,
  name,
  icon,
  error,
  label,
  value,
  className,
  onChange,
  isLoading,
  options,
  disabled,
  defaultValue,
  positionMenu,
  classNameLabel,
  placeholder,
  optionStyles,
  selectStyles = {},
  isMulti,
  isClearable,
  rounded = 'full',
  withBorder = false,
  showBorderBottom = true,
  autoHeight = true,
  bgTransparent = false,
  position = 'relative',
}: SelectProps<T, Multi, Clearable>) => {
  const multi = isMulti ?? false;
  const clearable = isClearable ?? false;
  const generatedId = useId();

  const [inputValue, setInputValue] = useState('');

  const handleOnInputChange = (newValue: string) => {
    setInputValue(newValue);
  };

  const handleOnBlur = (e: FocusEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (!inputValue.trim() || !multi) return;

    setInputValue('');
  };

  const handleChange = useCallback(
    (item: SelectOption<T> | SelectOption<T>[] | null) => {
      if (!onChange) return;

      if (multi) {
        // MULTI
        const values = isArray(item) ? item.map(({ value }) => value) : null;

        (onChange as (values: T[] | null) => void)(values);
      } else {
        // SINGLE
        const value = !isArray(item) && item ? item.value : null;

        (onChange as (value: T | null) => void)(value);
      }
    },
    [onChange, multi],
  );

  const singleValue = useMemo(() => {
    if (value === undefined) return null;

    const normalized = normalize(value);
    return options.find(opt => normalize(opt.value) === normalized) || null;
  }, [options, value]);

  const multiValue = useMemo(() => {
    if (!multi || !isArray(value)) return [];

    const normalizedValues = value.map(v => normalize(v));
    return options.filter(opt => normalizedValues.includes(normalize(opt.value)));
  }, [multi, options, value]);

  // const singleValue = useMemo(
  //   () =>
  //     (value !== undefined
  //       ? options.find(item => item.value?.toString() === value?.toString())
  //       : value) || null,
  //   [options, value],
  // );
  //
  // const multiValue = useMemo(() => {
  //   if (!multi || !isArray(value)) return [];
  //
  //   return options.filter(option => value.includes(option.value));
  // }, [multi, options, value]);

  const CurrentSelect = (
    <ReactSelect
      name={name}
      id={id}
      instanceId={id ?? generatedId}
      onBlur={handleOnBlur}
      placeholder={placeholder}
      filterOption={createFilter({
        matchFrom: 'any',
        stringify: option =>
          `${
            typeof option.label === 'string'
              ? option.label
              : typeof option.value === 'string'
              ? option.value
              : option?.toString() || String(option.value)
          }`,
      })}
      components={!multi ? { Option } : !options ? { Option, DropdownIndicator: null } : { Option }}
      onChange={handleChange as any}
      onInputChange={handleOnInputChange}
      inputValue={inputValue}
      defaultValue={defaultValue}
      value={multi ? multiValue : singleValue}
      options={options}
      styles={{
        ...styles({
          disabled,
          showBorderBottom,
          positionMenu,
          position,
          withBorder,
          autoHeight,
          bgTransparent,
          rounded,
          isClearable: clearable,
          option: optionStyles,
          isError: !!error,
        }),
        ...selectStyles,
      }}
      className={!(label !== undefined || icon || error) ? className : undefined}
      isLoading={isLoading}
      isDisabled={disabled}
      isMulti={multi}
      isClearable={multi || clearable}
    />
  );

  if (label !== undefined || icon || error) {
    return (
      <div
        className={classNames(
          'flex',
          {
            'flex-col': !className?.includes('flex-row'),
            'w-full': !className?.includes('w-'),
            relative: icon,
          },
          className,
        )}
      >
        {!!label && (
          <span
            className={classNames(
              'text-gray-900 font-[Inter] text-[10px] leading-[18px] opacity-64 uppercase mb-2',
              classNameLabel,
            )}
          >
            {label}
          </span>
        )}
        {CurrentSelect}
        {icon && (
          <span className="absolute inset-y-0 left-0 flex items-center pl-4">
            <img src={icon} alt={name || 'icon'} className="h-4 w-4 min-w-[16px] min-h-[16px]" />
          </span>
        )}
        {error && <span className="mt-2 text-sm text-error-red">{error}</span>}
      </div>
    );
  }

  return CurrentSelect;
};
